import _ from 'lodash';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import momentDurationFormatSetup from 'moment-duration-format';

//- Material UI / Style
import { withStyles } from '@material-ui/core/styles';

//containers
import AuthUserContainer from '../containers/AuthUser';
import UserContainer from '../containers/User';

//- UI / Form
import FormText from '../components/form/FormText';

import SectionHeader from '../components/SectionHeader';
import CompanyDepartmentPicker from '../components/advancedForm/CompanyDepartmentPicker';
import PermissionPicker from '../components/advancedForm/PermissionPicker';
import FormContainer from '../components/FormContainer';
import Note from '../components/Note';
import FormCheckbox from '../components/form/FormCheckbox';
import FormActions from '../components/form/FormActions';
import { Grid } from '@material-ui/core';
import UserPicker from '../components/advancedForm/UserPicker';
import FormSelect from '../components/form/FormSelect';

momentDurationFormatSetup(moment);

const styles = () => {
	return {};
};

class SaveUser extends Component {
	constructor(props) {
		super(props);

		const action = _.get(this.props, 'match.params.action');
		const userId = parseInt(_.get(this.props, 'match.params.userId'), 10);
		const isUpdate = action === 'update';

		this.state = {
			isUpdate,
			userId,

			formFieldError: {},
			companyDepartmentMap: {},
			permissionMap: {},

			isSavingOpen: false,
			savingStatus: undefined,
		};
	}

	static getDerivedStateFromProps(props, state) {
		const { user } = props;

		const { isUpdate } = state;

		const isDriver = isUpdate && user && user.role === 'driver';

		return {
			isDriver,
		};
	}

	componentDidMount() {
		const { user, getUser } = this.props;
		const { userId, isUpdate } = this.state;

		if (!isUpdate) {
			return;
		}

		if (user) {
			this.fillForm({ user });
		} else {
			getUser({
				params: { userIds: [userId] },
			});
		}
	}

	componentDidUpdate(prevProps, prevState, snapShot) {
		const { user } = this.props;

		if (_.isUndefined(prevProps.user) && !_.isUndefined(user)) {
			this.fillForm({ user });
		}
	}

	fillForm = ({ user }) => {
		if (!user) {
			return;
		}

		const newState = {};
		[
			'email',
			'firstName',
			'lastName',
			'title',
			'mobilePhoneNumber',
			'officePhoneNumber',
			'exponentHrEmployeeName',
			'userClocksInAndOut',
			'supervisorUserId',
			'hoursInSingleDayForFlag',
			'shiftsDoNotGetExported',
			'doNotFlagMorningLessThanFiveHours',
		].forEach((key) => {
			if (_.isNil(user[key])) {
				return;
			}
			newState[key] = user[key];
		});

		const { companyDepartmentIds, permissions } = user;
		newState.companyDepartmentMap = {};
		_.forEach(companyDepartmentIds, (id) => {
			newState.companyDepartmentMap[id] = true;
		});

		newState.permissionMap = {};
		_.forEach(permissions, (id) => {
			if (id === 'canViewBetaPto') {
				return;
			}
			newState.permissionMap[id] = true;
		});

		this.setState(newState);
	};

	//- Path is always a string
	//- value is never an event (value.target)
	onChange = (value, path) => {
		const arrayPath = _.toPath(path);
		const basePath = _.head(arrayPath);
		let base = this.state[basePath];
		let newState = {};

		if (_.isArray(base) || _.isObject(base)) {
			base = _.cloneDeep(base);
			_.set(base, arrayPath.slice(1), value);
			newState[basePath] = base;
		} else {
			newState[basePath] = value;
		}

		//- Clear any errors on the path
		let formFieldError = Object.assign({}, this.state.formFieldError);
		delete formFieldError[path];
		newState.formFieldError = formFieldError;

		this.setState(newState);
	};

	closeSavingDialog = () => {
		this.setState({ onContinue: false });
	};

	goBackToList = () => {
		this.props.history.push(`/users`);
	};

	handleSave = ({ callback }) => {
		const { addUser, updateUser, showErrors } = this.props;

		const {
			isUpdate,
			userId,
			userClocksInAndOut,
			exponentHrEmployeeName,
			supervisorUserId,
			shiftsDoNotGetExported,
			doNotFlagMorningLessThanFiveHours,
		} = this.state;

		const formFieldError = {};
		const requiredFields = [
			{ path: 'email', label: 'Email' },
			{ path: 'firstName', label: 'First name' },
			{ path: 'lastName', label: 'Last name' },
		];

		if (!isUpdate) {
			requiredFields.push(
				{ path: 'password', label: 'Password' },
				{ path: 'retypePassword', label: 'Retype Password' }
			);
		}

		requiredFields.forEach(({ path, label }) => {
			if (!this.state[path]) {
				formFieldError[path] = `${label} is required`;
			}
		});

		const {
			email,
			password,
			retypePassword,
			firstName,
			lastName,
			title,
			officePhoneNumber,
			mobilePhoneNumber,
			companyDepartmentMap,
			permissionMap,
			hoursInSingleDayForFlag,
		} = this.state;

		if (!isUpdate && password !== retypePassword) {
			formFieldError.password = `Passwords don't match`;
			formFieldError.retypePassword = `Passwords don't match`;
		}

		const companyDepartmentIds = [];
		_.keys(companyDepartmentMap).forEach((key) => {
			if (companyDepartmentMap[key]) {
				companyDepartmentIds.push(parseInt(key, 10));
			}
		});

		if (_.isEmpty(companyDepartmentIds)) {
			formFieldError.companyDepartmentMap = `You must choose at least one department`;
		}

		const permissions = [];
		_.keys(permissionMap).forEach((key) => {
			if (permissionMap[key]) {
				permissions.push(key);
			}
		});

		if (userClocksInAndOut && !exponentHrEmployeeName) {
			formFieldError.exponentHrEmployeeName = `If user clocks in and out they need an ExponentHR Employee Name`;
		}

		if (userClocksInAndOut && !supervisorUserId) {
			formFieldError.supervisorUserId = `If user clocks in and out they need supervisor`;
		}

		//- Get all the values and do individual checks
		if (_.keys(formFieldError).length) {
			this.setState({ formFieldError });
			showErrors(_.values(formFieldError));
			return;
		}

		const params = {
			email,
			password,
			firstName,
			lastName,
			title,
			officePhoneNumber,
			mobilePhoneNumber,
			companyDepartmentIds,
			permissions,
			userClocksInAndOut,
			exponentHrEmployeeName,
			supervisorUserId,
			shiftsDoNotGetExported,
			hoursInSingleDayForFlag,
			doNotFlagMorningLessThanFiveHours,
		};

		this.setState({
			isSavingOpen: true,
			savingStatus: 'saving',
		});

		if (!isUpdate) {
			addUser({ params, callback });
		} else {
			params.userId = userId;
			updateUser({ params, callback });
		}
	};

	handleDelete = ({ callback }) => {
		const { removeUser } = this.props;
		const { userId } = this.state;

		removeUser({
			userId,
			callback,
		});
	};

	getInputProps = (path, defaultValue) => {
		return {
			key: path,
			path,
			value: this.getValue(path, defaultValue),
			checked: this.getValue(path, defaultValue),
			error: this.getError(path),
			onChange: this.onChange,
			gridProps: { md: 12 },
		};
	};

	getValue = (path, defaultValue) => {
		const _d = _.isUndefined(defaultValue) ? '' : defaultValue;
		return _.get(this.state, path, _d);
	};

	getError = (path) => {
		return _.get(this.state.formFieldError, path);
	};

	renderRegularUserContent() {
		return (
			<Fragment>
				<SectionHeader label="Login" gridProps={{ md: 12 }} />

				<FormText label={'Email'} helpText={'Used for login'} {...this.getInputProps('email')} />

				<FormText label={'Password'} inputType={'password'} {...this.getInputProps('password')} />

				<FormText
					label={'Retype password'}
					inputType={'password'}
					{...this.getInputProps('retypePassword')}
				/>

				<SectionHeader label="Info" gridProps={{ md: 12 }} />

				<FormText {...this.getInputProps('firstName')} label={'First Name'} gridProps={{ sm: 6 }} />

				<FormText {...this.getInputProps('lastName')} label={'Last Name'} gridProps={{ sm: 6 }} />

				<FormText label={'Title'} {...this.getInputProps('title')} />

				<FormText
					{...this.getInputProps('mobilePhoneNumber')}
					label={'Mobile Phone #'}
					gridProps={{ sm: 6 }}
				/>

				<FormText
					{...this.getInputProps('officePhoneNumber')}
					label={'Office Phone #'}
					gridProps={{ sm: 6 }}
				/>

				<SectionHeader label="Time Clocks" gridProps={{ md: 12 }} />

				<FormCheckbox
					{...this.getInputProps('userClocksInAndOut', false)}
					label={'Employee clocks in and clocks out'}
					gridProps={{ sm: 6 }}
				/>

				<FormCheckbox
					{...this.getInputProps('shiftsDoNotGetExported', false)}
					label={"Employee's shifts are not exported to exponent"}
					gridProps={{ sm: 6 }}
					helpText={'Used for Temp employees that are not paid through exponent'}
				/>

				<FormText
					{...this.getInputProps('exponentHrEmployeeName')}
					label={'ExponentHR Employee Name'}
					gridProps={{ sm: 6 }}
				/>

				<UserPicker
					{...this.getInputProps('supervisorUserId')}
					label="Supervisor"
					helpText="Who will get alerted when shifts get flagged"
					whoToShow={'nonDriversOnly'}
					gridProps={{ sm: 6 }}
				/>
				<FormSelect
					{...this.getInputProps('hoursInSingleDayForFlag')}
					label={"Flag employee's shift if over 8/10/12 hours in a day"}
					gridProps={{ sm: 6 }}
					helpText={
						'Default for non-drivers is 10 hours. 8 used for billing, 12 used for coordinates and mechanics'
					}
					options={[
						{ value: 8, display: '8 hours' },
						{ value: 10, display: '10 hours' },
						{ value: 12, display: '12 hours' },
					]}
				/>

				<FormCheckbox
					{...this.getInputProps('doNotFlagMorningLessThanFiveHours', false)}
					label={'Do not flag if first shift is less than 5 hours'}
					gridProps={{ sm: 6 }}
					helpText={'Used for Washington employees that can waive their first meal period.'}
				/>

				<CompanyDepartmentPicker {...this.getInputProps('companyDepartmentMap')} />

				<PermissionPicker {...this.getInputProps('permissionMap')} />
			</Fragment>
		);
	}

	render() {
		const { permissions, user, showErrors } = this.props;

		const { isUpdate, isDriver } = this.state;

		if (!permissions || !_.includes(permissions, 'canAddAndUpdateUsers')) {
			return <div>You do not have valid permissions to view this page.</div>;
		}
		if (isUpdate && !user) {
			return null;
		}

		const action = isUpdate ? 'Update' : 'Add';
		return (
			<FormContainer headline={`${action} User`}>
				{!isDriver && this.renderRegularUserContent()}

				{isDriver && (
					<h2>
						{user.firstName} {user.lastName}
					</h2>
				)}
				{isDriver && (
					<Note
						label={`All drivers are synced from GeoTabs and generic fields cannot be updated here.
				All updates must be made in GeoTabs and will be synced with Sphere every 10 minutes.`}
					/>
				)}

				{!isDriver && (
					<Grid item xs={12}>
						<FormActions
							onSave={this.handleSave}
							onCancel={this.goBackToList}
							onDone={this.goBackToList}
							onDelete={isUpdate ? this.handleDelete : undefined}
							onError={showErrors}
							saveButtonText={`${action} User`}
							saveProgressText={isUpdate ? 'Updating user' : 'Creating new user'}
							saveSuccessText={'User saved successfully. Thank you.'}
							deleteButtonText={'Terminate User'}
						/>
					</Grid>
				)}
			</FormContainer>
		);
	}
}

SaveUser.propTypes = {
	addUser: PropTypes.func.isRequired,
	updateUser: PropTypes.func.isRequired,
	removeUser: PropTypes.func.isRequired,
	user: PropTypes.object,

	showErrors: PropTypes.func.isRequired,
	authUser: PropTypes.object,
	classes: PropTypes.object.isRequired,
};

export default _.flow([withStyles(styles), AuthUserContainer, UserContainer])(SaveUser);
